import React, { useEffect, useState } from 'react';
import { contactApi } from '../services';
import accessApi from '../services/accessApi';
import { formatDateTime, timeAgo } from '../utils/time';
import AccordionSection from './AccordionSection';
import Icon from './Icon';
import './RelationshipManagers.css';

export const managerInitials = (name = '') => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part[0].toUpperCase())
  .join('');

/**
 * Who manages the relationship with a company, shown in the company panel and changed in
 * place: tick people on the master list, or add someone new (they join the list on save).
 */
const RelationshipManagersField = ({ companyId, companyName, managers = [], onChange }) => {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  // Choices while editing: the master list's active people, those who manage the company now, and newcomers
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState([]);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // The latest change to who manages the company, from the audit log
  const [lastChange, setLastChange] = useState(null);
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [companyId]);

  useEffect(() => {
    setLastChange(null);
    if (!companyId) return undefined;
    let cancelled = false;
    accessApi.activity({ subject_type: 'company', subject_id: companyId, action: 'company.managers_changed', limit: 1 })
      .then(data => {
        if (!cancelled) setLastChange(data.events[0] || null);
      })
      // Only a hint: without it the section still says who manages the company
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [companyId, savedCount]);

  const startEditing = async () => {
    const assigned = new Set(managers.map(manager => manager.id));
    setOpen(true);
    setEditing(true);
    setSelected([...assigned]);
    setNewName('');
    setError(null);
    setOptions(null);
    try {
      const all = await contactApi.getRelationshipManagers(true);
      setOptions(all.filter(manager => manager.active || assigned.has(manager.id)));
    } catch (err) {
      setError(err.message);
      setOptions([]);
    }
  };

  const toggle = (optionId) => {
    setSelected(current => (current.includes(optionId)
      ? current.filter(id => id !== optionId)
      : [...current, optionId]));
  };

  const addNewcomer = (event) => {
    event.preventDefault();
    const name = newName.trim().replace(/\s+/g, ' ');
    if (!name) return;
    const existing = options.find(option => option.name.toLowerCase() === name.toLowerCase());
    const option = existing || { id: `new:${name.toLowerCase()}`, name, active: true, isNew: true };
    if (!existing) setOptions(current => [...current, option]);
    setSelected(current => (current.includes(option.id) ? current : [...current, option.id]));
    setNewName('');
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const managerIds = [];
      for (const id of selected) {
        const option = options.find(item => item.id === id);
        if (!option?.isNew) {
          managerIds.push(id);
          continue;
        }
        const created = await contactApi.createRelationshipManager({ name: option.name });
        // The newcomer is on the list now, so trying again after an error doesn't add them twice
        setOptions(current => current.map(item => (item.id === id ? created : item)));
        setSelected(current => current.map(item => (item === id ? created.id : item)));
        managerIds.push(created.id);
      }
      onChange(await contactApi.setCompanyRelationshipManagers(companyId, managerIds));
      setSavedCount(count => count + 1);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const fromWorkbook = managers.length > 0 && managers.every(manager => manager.source === 'icp-import');

  return (
    <AccordionSection
      id="rm-field"
      className="rm-field"
      open={open}
      onToggle={() => setOpen(current => !current)}
      title={`Relationship ${managers.length === 1 && !editing ? 'manager' : 'managers'}`}
      actions={!editing && (
        <button
          type="button"
          className="rm-icon-button"
          onClick={startEditing}
          aria-label={`Change who manages ${companyName || 'this company'}`}
          title="Change"
        >
          <Icon name="pencil" />
        </button>
      )}
    >
      {!editing && managers.length > 0 && (
        <>
          <ul className="rm-people">
            {managers.map(manager => (
              <li key={manager.id} className="rm-person">
                <span className="rm-avatar" aria-hidden="true">{managerInitials(manager.name)}</span>
                <span className="rm-person__text">
                  <span className="rm-person__name">
                    {manager.name}
                    {!manager.active && <span className="rm-badge">Inactive</span>}
                  </span>
                  {(manager.title || manager.email) && (
                    <span className="rm-person__meta">
                      {manager.title}
                      {manager.title && manager.email ? ' · ' : ''}
                      {manager.email && <a href={`mailto:${manager.email}`}>{manager.email}</a>}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {lastChange ? (
            <p className="rm-field__note">
              Changed by {lastChange.actor.name}{' '}
              <span title={formatDateTime(lastChange.occurred_at)}>{timeAgo(lastChange.occurred_at)}</span>
            </p>
          ) : fromWorkbook && <p className="rm-field__note">From the Owner column of the ICP workbook</p>}
        </>
      )}

      {!editing && managers.length === 0 && (
        <p className="rm-field__empty">
          Nobody yet.{' '}
          <button type="button" className="rm-link" onClick={startEditing}>Assign someone</button>
        </p>
      )}

      {editing && (
        <div className="rm-picker">
          {options === null ? (
            <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>
          ) : (
            <fieldset className="rm-picker__options" disabled={saving}>
              <legend className="rm-visually-hidden">Who manages {companyName || 'this company'}</legend>
              {options.length === 0 && (
                <p className="placeholder-text">Nobody is on the list yet; add someone below.</p>
              )}
              {options.map(option => (
                <label key={option.id} className="rm-picker__option">
                  <input
                    type="checkbox"
                    checked={selected.includes(option.id)}
                    onChange={() => toggle(option.id)}
                  />
                  <span className="rm-picker__label">
                    {option.name}
                    {option.title && <span className="rm-picker__title"> · {option.title}</span>}
                    {option.isNew && <span className="rm-badge rm-badge--new">New</span>}
                    {!option.active && <span className="rm-badge">Inactive</span>}
                  </span>
                </label>
              ))}
            </fieldset>
          )}

          <form className="rm-picker__add" onSubmit={addNewcomer}>
            <input
              type="text"
              className="rm-input"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Someone not on the list"
              aria-label="Name of someone not on the list"
              maxLength={120}
              disabled={saving || options === null}
            />
            <button type="submit" className="rm-button" disabled={!newName.trim() || saving || options === null}>
              <Icon name="plus" /> Add
            </button>
          </form>

          {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}

          <div className="rm-buttons">
            <button
              type="button"
              className="rm-button rm-button--primary"
              onClick={save}
              disabled={saving || options === null}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="rm-button" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </AccordionSection>
  );
};

export default RelationshipManagersField;
